/**
 * Calendar dates and durations the way the app reasons about them (docs/01-architectuur/04-datamodel.md, Tijd en
 * duur): instants are UTC, a "day" is a local date in Europe/Amsterdam, a duration is whole seconds. Pure functions,
 * shared by the server (time entries, week grid) and the web app (forms, formatting).
 */
export const DEFAULT_TZ = 'Europe/Amsterdam';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function wallClock(instant: Date, tz: string) {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(instant)) p[x.type] = x.value;
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day), h: Number(p.hour) % 24, mi: Number(p.minute), s: Number(p.second) };
}

/** The offset of a time zone at an instant, in minutes east of UTC (Amsterdam: 60 in winter, 120 in summer). */
export function tzOffsetMinutes(instant: Date, tz = DEFAULT_TZ): number {
  const w = wallClock(instant, tz);
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s);
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/** The local date (YYYY-MM-DD) of an instant. */
export function zonedDate(instant: Date, tz = DEFAULT_TZ): string {
  const w = wallClock(instant, tz);
  return `${w.y}-${String(w.m).padStart(2, '0')}-${String(w.d).padStart(2, '0')}`;
}

/** The local wall-clock time of an instant, as minutes since midnight. */
export function zonedMinutes(instant: Date, tz = DEFAULT_TZ): number {
  const w = wallClock(instant, tz);
  return w.h * 60 + w.mi;
}

/** The instant of a local wall-clock time on a date; across a DST gap the clock jumps forward. */
export function zonedDateTime(date: string, minutesSinceMidnight: number, tz = DEFAULT_TZ): Date {
  const m = ISO_DATE.exec(date);
  if (!m) throw new Error(`Not an ISO date: ${date}`);
  const guess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0) + minutesSinceMidnight * 60_000;
  const first = tzOffsetMinutes(new Date(guess), tz);
  let t = guess - first * 60_000;
  const second = tzOffsetMinutes(new Date(t), tz);
  if (second !== first) t = guess - second * 60_000;
  return new Date(t);
}

/** Midnight at the start of a local date. */
export const zonedMidnight = (date: string, tz = DEFAULT_TZ): Date => zonedDateTime(date, 0, tz);

/** A date plus a number of days (calendar arithmetic, no time zone involved). */
export function addDays(date: string, days: number): string {
  const m = ISO_DATE.exec(date);
  if (!m) throw new Error(`Not an ISO date: ${date}`);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return d.toISOString().slice(0, 10);
}

/** Days from a to b (b minus a). */
/**
 * A date plus a number of workdays, Monday to Friday; negative goes back. A start on a weekend counts from the next or
 * previous workday. Public holidays count as workdays for now (docs/04-domeinen/proposals.md, Open punten).
 */
export function addWorkdays(date: string, workdays: number): string {
  const step = workdays < 0 ? -1 : 1;
  let d = date;
  let left = Math.abs(workdays);
  while (left > 0) {
    d = addDays(d, step);
    if (isoWeekday(d) <= 5) left--;
  }
  return d;
}

export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** ISO weekday of a date: 1 = Monday … 7 = Sunday. */
export function isoWeekday(date: string): number {
  const d = new Date(`${date}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

/** The first day of the week a date falls in; the week starts on Monday (1) unless the person prefers another day. */
export function weekStart(date: string, startsOn = 1): string {
  return addDays(date, -((isoWeekday(date) - startsOn + 7) % 7));
}

/** The seven dates of the week starting on a day. */
export function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** Today as a local date. */
export const today = (tz = DEFAULT_TZ, now = new Date()): string => zonedDate(now, tz);

/**
 * Parses a duration as people type it: "1:30", "1,5", "1.5", "90m", "1u30", "2h", "45" (minutes when no unit and < 10
 * is read as hours). Returns seconds, or null when it cannot be read.
 */
export function parseDuration(input: string): number | null {
  const s = input.trim().toLowerCase().replaceAll(' ', '');
  if (!s) return null;
  let m = s.match(/^(\d{1,3}):([0-5]?\d)$/);
  if (m) return Number(m[1]) * 3600 + Number(m[2]) * 60;
  m = s.match(/^(\d{1,3})(?:u|h)(?:([0-5]?\d)m?)?$/);
  if (m) return Number(m[1]) * 3600 + Number(m[2] ?? 0) * 60;
  m = s.match(/^(\d{1,4})m(in)?$/);
  if (m) return Number(m[1]) * 60;
  m = s.match(/^(\d{1,3})[.,](\d{1,2})$/);
  if (m) return Math.round(Number(`${m[1]}.${m[2]}`) * 3600);
  m = s.match(/^(\d{1,4})$/);
  if (m) {
    const n = Number(m[1]);
    return n < 10 ? n * 3600 : n * 60;
  }
  return null;
}

const isoDate = (y: number, m: number, d: number) => `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** Easter Sunday of a year (the anonymous Gregorian algorithm). */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return isoDate(year, month, day);
}

/** The Dutch public holidays of a year on which an office is closed: New Year, Easter Monday, King's Day, Ascension, Whit Monday, Christmas. */
export function dutchHolidays(year: number): Set<string> {
  const easter = easterSunday(year);
  const kingsDay = new Date(Date.UTC(year, 3, 27)).getUTCDay() === 0 ? isoDate(year, 4, 26) : isoDate(year, 4, 27);
  return new Set([isoDate(year, 1, 1), addDays(easter, 1), kingsDay, addDays(easter, 39), addDays(easter, 50), isoDate(year, 12, 25), isoDate(year, 12, 26)]);
}
